import { isSecureWindowContext } from "appConfiguration";
import type { SupportedLocale } from "staticConfiguration";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box, { type BoxProps } from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FilePreview, isPreviewableFile } from "core/components/FilePreview";
import { useFormatDate } from "core/hooks/useFormatDate";
import { useGetPitstopUrl } from "core/hooks/useGetPitstopUrl";
import { useLocale } from "core/hooks/useLocale";
import { useLocaleFormatter } from "core/hooks/useLocaleFormatter";
import { useLogComponentMount } from "core/hooks/useLogComponentMount";
import { useToaster } from "core/hooks/useToaster";
import { copyTextToClipboard } from "core/utils/copyTextToClipboard";
import type { DocNodeSummaryWithParentsDto } from "features/DMS/entities";
import { useDmsDocumentHref } from "features/DMS/hooks";
import { getParentPath } from "features/DMS/utils/getParentPath";
import { FavoriteButton } from "features/MyLinks/components";
import { useNotifications } from "features/Notifications/useNotifications";
import { type Dispatch, type SetStateAction, useEffect } from "react";
import { useTranslation } from "react-i18next";

export function DocNodeDocumentDisplay({
  node,
  setSelectedLocale,
}: {
  node: DocNodeSummaryWithParentsDto;
  setSelectedLocale?: Dispatch<SetStateAction<SupportedLocale>>;
  boxProps?: BoxProps;
}) {
  const locale = useLocale();
  useLogComponentMount("DmsDocNodeDocument", node.id!);
  const formatLocale = useLocaleFormatter();
  const { markNotificationAsReadByItemId } = useNotifications();
  useEffect(() => {
    markNotificationAsReadByItemId(node.id!);
  }, [node.id, markNotificationAsReadByItemId]);
  const canPreview = node.fileName ? isPreviewableFile(node.fileName) : false;
  const href = useDmsDocumentHref(
    node.fileId,
    node.fileName,
    node.documentLink,
    canPreview ? "inline" : "attachment"
  );
  const formatDate = useFormatDate({ dateStyle: "short" });
  const { t } = useTranslation("DocumentManagementSystem", {
    keyPrefix: "Next:DocumentManagementSystem:DocumentPageDisplay",
  });
  const publishTimeFormatted = t("publishTimeLabel", {
    publishTime: node.publishTime && formatDate(node.publishTime),
  });
  const availableToFormatted = t("availableToLabel", {
    availableTo: node.availableTo && formatDate(node.availableTo),
  });
  const { successToast } = useToaster();
  const getPitstopUrl = useGetPitstopUrl();
  return (
    <>
      <Stack direction="row" flexWrap="wrap">
        <Stack flex={1}>
          <Box>
            <Typography variant="h5">
              {node.name}
              {node.path && node.name && (
                <FavoriteButton
                  itemType="DmsDocNodeDocument"
                  itemId={node.path}
                  title={node.name}
                />
              )}
            </Typography>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              maxWidth="60ch"
            >
              {node.description}
            </Typography>
          </Box>
          <Box>
            {node.publishTime && (
              <Typography variant="body2" color="text.secondary">
                {publishTimeFormatted ?? " "}
              </Typography>
            )}
            {node.availableTo && (
              <Typography variant="body2" color="text.secondary">
                {availableToFormatted ?? " "}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {t("availableLocalesLabel", "Available locales:")}
            </Typography>
            <ButtonGroup>
              {Array.isArray(node.locales) &&
                node.locales
                  .sort((a, b) => {
                    if (a === locale) {
                      return -1;
                    }
                    if (b === locale) {
                      return 1;
                    }
                    const localeA = formatLocale(a) ?? a;
                    const localeB = formatLocale(b) ?? b;
                    return localeA.localeCompare(localeB);
                  })
                  .map((loc) => (
                    <Button
                      size="small"
                      key={loc}
                      onClick={() =>
                        setSelectedLocale?.(loc as SupportedLocale)
                      }
                      disabled={loc === node.currentLocale}
                      variant={
                        loc === node.currentLocale ? "contained" : "outlined"
                      }
                    >
                      {formatLocale(loc)}
                    </Button>
                  ))}
            </ButtonGroup>
          </Box>
        </Stack>
        <Stack gap={1}>
          {isSecureWindowContext && (
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={async () => {
                const url = getPitstopUrl(
                  `/dms/library/v2${getParentPath(node.path ?? "")}`
                );
                url.searchParams.set("action", "document-view");
                url.searchParams.set("actionPath", node.path ?? "");
                await copyTextToClipboard(url.href);
                successToast(t("linkCopySuccess", "Link copied to clipboard"));
              }}
            >
              {t("copyLink", "Copy Pitstop Link")}
            </Button>
          )}
          {!node.documentLink && (
            <Button
              href={href}
              target="_blank"
              variant="outlined"
              startIcon={<DownloadIcon />}
            >
              {t("download", "Download")}
            </Button>
          )}
        </Stack>
      </Stack>
      <Divider sx={{ my: 2 }} />
      {node.fileId && node.fileName && canPreview ? (
        <FilePreview fileName={node.fileName} src={href} />
      ) : (
        <Button
          href={href}
          target="_blank"
          variant="outlined"
          startIcon={<OpenInNewIcon />}
        >
          {t("visitLink")}
        </Button>
      )}
    </>
  );
}
